import React, { useState, useEffect, useCallback,useMemo } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'
import {
  hasSalesQuotePdf,
  resolveSqPdfDownloadUrl
} from '../utils/sqPdfDownload'

const API_URL = process.env.REACT_APP_API_URL


function parseJsonIfString(value) {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

function parseApiResponse(raw) {
  const d = parseJsonIfString(raw)
  if (d?.body != null) return parseJsonIfString(d.body)
  return d
}

function isApiSuccess(res) {
  if (!res || typeof res !== 'object') return false
  const s = res.success
  return s === true || s === 'true' || s === 1 || s === '1'
}

function pickQuoteFromResponse(res, id) {
  const block = parseJsonIfString(res?.data != null ? res.data : res)
  if (!block || typeof block !== 'object' || Array.isArray(block)) return null
  if (block.quote && typeof block.quote === 'object') return block.quote
  if (Array.isArray(block.quotes) && block.quotes.length > 0) {
    return (
      block.quotes.find((q) => String(q._id || q.id) === String(id)) ||
      block.quotes[0]
    )
  }
  if (
    block.quoteNumber != null ||
    block._id != null ||
    block.materials != null ||
    block.terms != null
  ) {
    return block
  }
  return null
}

function getMaterialGstPercent(row) {
  if (row?.gstPercent != null && row.gstPercent !== '') return row.gstPercent
  if (row?.gst != null && row.gst !== '') return row.gst
  return null
}

function formatMaterialMoney(value) {
  if (value == null || value === '') return '-'
  const n = parseFloat(value)
  return Number.isFinite(n) ? n.toFixed(2) : String(value)
}

function getMaterialGstValueDisplay(row) {
  if (row?.gstValue != null && row.gstValue !== '') {
    return formatMaterialMoney(row.gstValue)
  }
  const basic = parseFloat(row?.basicPrice) || 0
  const qty = parseFloat(row?.quantity) || 0
  const gst = parseFloat(getMaterialGstPercent(row)) || 0
  if (basic > 0 && qty > 0 && gst > 0) {
    return (basic * qty * (gst / 100)).toFixed(2)
  }
  return '-'
}

function getMaterialTotalValueDisplay(row) {
  if (row?.totalValue != null && row.totalValue !== '') {
    return formatMaterialMoney(row.totalValue)
  }
  const basic = parseFloat(row?.basicPrice) || 0
  const qty = parseFloat(row?.quantity) || 0
  const gst = parseFloat(getMaterialGstPercent(row)) || 0
  if (basic > 0 && qty > 0) {
    const totalBasic = basic * qty
    return (totalBasic + totalBasic * (gst / 100)).toFixed(2)
  }
  return '-'
}

function calcGrossTotalsFromMaterials(materialRows) {
  return materialRows.reduce(
    (acc, row) => {
      const lineBasic =
        (parseFloat(row?.basicPrice) || 0) * (parseFloat(row?.quantity) || 0)
      let gstValue = parseFloat(row?.gstValue)
      if (!Number.isFinite(gstValue)) {
        const gst = parseFloat(getMaterialGstPercent(row)) || 0
        gstValue = (lineBasic * gst) / 100
      }
      let totalValue = parseFloat(row?.totalValue)
      if (!Number.isFinite(totalValue)) totalValue = lineBasic + gstValue
      return {
        grossTotalBasicPrice: acc.grossTotalBasicPrice + lineBasic,
        grossGstValue: acc.grossGstValue + gstValue,
        grossTotalValue: acc.grossTotalValue + totalValue
      }
    },
    { grossTotalBasicPrice: 0, grossGstValue: 0, grossTotalValue: 0 }
  )
}

function getQuoteGrossTotals(quote, materialRows) {
  const basic = Number(quote?.grossTotalBasicPrice)
  const gst = Number(quote?.grossGstValue)
  const total = Number(quote?.grossTotalValue)
  if (Number.isFinite(basic) && Number.isFinite(gst) && Number.isFinite(total)) {
    return {
      grossTotalBasicPrice: basic,
      grossGstValue: gst,
      grossTotalValue: total
    }
  }
  return calcGrossTotalsFromMaterials(materialRows)
}

function formatDate(value) {
  if (!value) return '-'
  const str = String(value).trim()
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str.replace(/-/g, '/')
  const m = str.match(/^(\d{2})-(\d{2})-(\d{4})[ T]/)
  if (m) return `${m[1]}/${m[2]}/${m[3]}`
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return str || '-'
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(d)
  const get = (type) => parts.find((p) => p.type === type)?.value || ''
  return `${get('day')}/${get('month')}/${get('year')}`
}

function formatDateTime(value) {
  if (!value) return '-'
  const str = String(value).trim()
  const m = str.match(/^(\d{2})-(\d{2})-(\d{4})[ T](\d{2}):(\d{2})/)
  if (m) {
    const [, dd, mm, yyyy, hh, min] = m
    const hour = parseInt(hh, 10)
    const period = hour >= 12 ? 'PM' : 'AM'
    const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
    return `${dd}/${mm}/${yyyy}, ${String(h12).padStart(2, '0')}:${min} ${period}`
  }
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return str || '-'
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).formatToParts(d)
  const get = (type) => parts.find((p) => p.type === type)?.value || ''
  return `${get('day')}/${get('month')}/${get('year')}, ${get('hour')}:${get('minute')} ${get('dayPeriod').toUpperCase()}`
}

function getQuoteTermsRows(quote) {
  if (Array.isArray(quote?.terms) && quote.terms.length > 0) {
    return quote.terms.map((t) => ({
      quoteTerm: t.quoteTerm || t.rfqTerm || '-',
      quoteResponse: t.quoteResponse || t.termsResponse || '-'
    }))
  }
  return []
}

function mapAttachmentFromApi(item) {
  return {
    s3Key: item?.s3Key || '',
    originalFileName: item?.originalFileName || '',
    contentType: item?.contentType || '',
    uploadedAt: item?.uploadedAt || '',
    url: item?.url || ''
  }
}

function getAttachmentLabel(att) {
  if (!att) return 'Attachment'
  if (att.originalFileName) return att.originalFileName
  const key = att.s3Key || ''
  if (!key) return 'Attachment'
  const parts = String(key).split('/')
  return parts[parts.length - 1] || key
}

function getFileExtension(fileName, contentType) {
  const name = String(fileName || '').trim()
  const dotExt = name.includes('.') ? name.split('.').pop().toLowerCase() : ''
  if (dotExt && dotExt.length <= 6) return dotExt

  const typeMap = {
    'application/pdf': 'pdf',
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/msword': 'doc',
    'text/plain': 'txt',
    'application/zip': 'zip'
  }
  return typeMap[String(contentType || '').toLowerCase()] || ''
}

function getFileTypeMeta(extension, contentType) {
  const ext = String(extension || getFileExtension('', contentType)).toLowerCase()

  if (ext === 'pdf') {
    return { icon: 'fa-file-pdf', color: '#dc3545', bg: '#fde8ea', label: 'PDF' }
  }
  if (ext === 'xlsx' || ext === 'xls' || ext === 'csv') {
    return { icon: 'fa-file-excel', color: '#198754', bg: '#e8f5ee', label: ext.toUpperCase() || 'EXCEL' }
  }
  if (ext === 'docx' || ext === 'doc') {
    return { icon: 'fa-file-word', color: '#0d6efd', bg: '#e7f1ff', label: ext.toUpperCase() || 'WORD' }
  }
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) {
    return { icon: 'fa-file-image', color: '#6f42c1', bg: '#f3ebff', label: ext.toUpperCase() || 'IMAGE' }
  }
  if (ext === 'zip' || ext === 'rar' || ext === '7z') {
    return { icon: 'fa-file-zipper', color: '#fd7e14', bg: '#fff3e6', label: ext.toUpperCase() || 'ZIP' }
  }
  if (ext === 'ppt' || ext === 'pptx') {
    return { icon: 'fa-file-powerpoint', color: '#d63384', bg: '#fce8f3', label: ext.toUpperCase() || 'PPT' }
  }
  if (ext === 'txt') {
    return { icon: 'fa-file-lines', color: '#6c757d', bg: '#f1f3f5', label: 'TXT' }
  }
  return {
    icon: 'fa-file',
    color: '#495057',
    bg: '#f8f9fa',
    label: ext ? ext.toUpperCase() : 'FILE'
  }
}

const ViewField = ({ label, children }) => (
  <div className="form-group row align-items-center">
    <label className="col-12 col-md-4 col-form-label">{label}</label>
    <div className="col-12 col-md-8">
      <div className="txtdsply">{children}</div>
    </div>
  </div>
)

const SectionTitle = ({ icon, title }) => (
  <div className="row mb-2 mt-3">
    <div className="col-12">
      <h5 className="text-primary border-bottom pb-2 mb-3">
        <i className={`fa-solid ${icon} mr-2`} />
        {title}
      </h5>
    </div>
  </div>
)

const AttachmentTileGrid = ({ attachments, downloadingS3Key, onDownload, disabled }) => {
  if (!attachments?.length) return null

  return (
    <div className="d-flex flex-wrap" style={{ gap: '16px' }}>
      {attachments.map((att) => {
        const fileName = getAttachmentLabel(att)
        const fileMeta = getFileTypeMeta(
          getFileExtension(fileName, att.contentType),
          att.contentType
        )
        const isDownloading = downloadingS3Key === att.s3Key
        return (
          <button
            key={att.s3Key}
            type="button"
            className="border-0 text-center"
            title={`Open ${fileName}`}
            onClick={() => onDownload(att.s3Key, att.url)}
            disabled={disabled || isDownloading}
            style={{
              width: '92px',
              padding: '10px 8px',
              borderRadius: '10px',
              backgroundColor: fileMeta.bg,
              border: `1px solid ${fileMeta.color}33`,
              cursor: disabled || isDownloading ? 'not-allowed' : 'pointer',
              opacity: isDownloading ? 0.7 : 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start'
            }}
          >
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                backgroundColor: '#fff',
                color: fileMeta.color,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                marginBottom: '8px'
              }}
            >
              {isDownloading ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                <i className={`fa-solid ${fileMeta.icon}`} />
              )}
            </div>
            <div
              title={fileName}
              style={{
                fontSize: '11px',
                lineHeight: 1.3,
                wordBreak: 'break-word',
                color: '#212529',
                width: '100%'
              }}
            >
              {fileName.length > 22 ? `${fileName.slice(0, 19)}...` : fileName}
            </div>
          </button>
        )
      })}
    </div>
  )
}

const CustomerSalesQuoteView = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [quote, setQuote] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isOpeningPdf, setIsOpeningPdf] = useState(false)
    const [downloadingS3Key, setDownloadingS3Key] = useState('')

  const ownerAttachments = useMemo(() => {
    if (!Array.isArray(quote?.quoteOwner)) return []
    return quote.quoteOwner.map(mapAttachmentFromApi).filter((att) => att.s3Key)
  }, [quote?.quoteOwner])

  const fetchQuote = useCallback(async () => {
    if (!id) {
      toast.error('Sales quote ID is missing', { autoClose: 2000 })
      navigate('/customerSalesQuoteList')
      return
    }

    setIsLoading(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'get-customer-sales-quote-id',
        currentUserId: localStorage.getItem('id') || undefined,
        id
      })
      const res = parseApiResponse(response.data)

      if (isApiSuccess(res)) {
        const row = pickQuoteFromResponse(res, id)
        if (row) {
          setQuote(row)
        } else {
          toast.error('Sales quote not found', { autoClose: 2000 })
          navigate('/customerSalesQuoteList')
        }
      } else {
        toast.error(res?.message || 'Failed to fetch sales quote details', {
          autoClose: 2000
        })
        navigate('/customerSalesQuoteList')
      }
    } catch (error) {
      console.error('Fetch customer sales quote error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      toast.error(
        errData?.message || 'An error occurred while fetching sales quote details',
        { autoClose: 2000 }
      )
      navigate('/customerSalesQuoteList')
    } finally {
      setIsLoading(false)
    }
  }, [id, navigate])

  useEffect(() => {
    fetchQuote()
  }, [fetchQuote])

  const handleViewPdf = async () => {
    if (!hasSalesQuotePdf(quote)) {
      toast.error('PDF is not available for this sales quote', { autoClose: 2000 })
      return
    }

    setIsOpeningPdf(true)
    try {
      const downloadUrl = await resolveSqPdfDownloadUrl({
        pdfDownloadUrl: quote.pdfDownloadUrl,
        pdfLink: quote.pdfLink,
        quoteId: id
      })
      setQuote((prev) =>
        prev && prev.pdfDownloadUrl === downloadUrl
          ? prev
          : { ...prev, pdfDownloadUrl: downloadUrl }
      )
      window.open(downloadUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error('Open sales quote PDF error:', error)
      toast.error(error.message || 'Failed to open PDF', { autoClose: 2000 })
    } finally {
      setIsOpeningPdf(false)
    }
  }

    const downloadOwnerAttachment = async (s3Key, fallbackUrl) => {
    if (!s3Key || downloadingS3Key) return

    setDownloadingS3Key(s3Key)
    try {
      const response = await axios.post(API_URL, {
        action: 'get-sq-owner-attachment-download-presigned-url',
        s3Keys: [s3Key],
        expiresIn: 3600,
        currentUserId: localStorage.getItem('id') || undefined
      })
      const res = parseApiResponse(response.data)
      if (!isApiSuccess(res)) {
        toast.error(res?.message || 'Failed to get download URL', { autoClose: 2000 })
        return
      }
      const files = res.files || res.data?.files || []
      const downloadUrl =
        files[0]?.downloadUrl ||
        files[0]?.url ||
        files[0]?.presignedUrl ||
        res.downloadUrl ||
        fallbackUrl
      if (!downloadUrl) {
        toast.error('Download URL not available', { autoClose: 2000 })
        return
      }
      window.open(downloadUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error('Download attachment error:', error)
      if (fallbackUrl) {
        window.open(fallbackUrl, '_blank', 'noopener,noreferrer')
      } else {
        toast.error('Failed to download attachment', { autoClose: 2000 })
      }
    } finally {
      setDownloadingS3Key('')
    }
  }

  const header = (
    <div className="content-header">
      <div className="container-fluid">
        <div className="row mb-2 align-items-center">
          <div className="col-6 col-sm-6">
            <h1 className="m-0 txt_lft">View Sales Quote</h1>
          </div>
          <div className="col-6 col-sm-6">
            <ol className="breadcrumb justify-content-end mb-0">
              <li className="breadcrumb-item">
                <Link to={'/customerSalesQuoteList'}>
                  <i className="fa-solid fa-arrow-left-long" /> Back
                </Link>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  )

  const wrapCard = (body) => (
    <div>
      {header}
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">{body}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  if (isLoading) {
    return wrapCard(
      <div className="card-body text-center py-5">
        <PulseLoader color="#1199B2" size={10} />
      </div>
    )
  }

  if (!quote) {
    return wrapCard(
      <div className="card-body text-center py-5">
        <p>Sales quote not found</p>
      </div>
    )
  }

  const materials = Array.isArray(quote.materials) ? quote.materials : []
  const materialGrossTotals = getQuoteGrossTotals(quote, materials)
  const quoteTermsRows = getQuoteTermsRows(quote)

  return wrapCard(
    <div className="card-body">
      {/* Quote Details */}
      <SectionTitle icon="fa-file-lines" title="Quote Details" />
      <ViewField label="Quotation Number">{quote.quoteNumber || '-'}</ViewField>
      <ViewField label="Quotation Date">{formatDate(quote.quoteDate)}</ViewField>
      <ViewField label="Quotation Validity Date">
        {formatDateTime(quote.quoteDeliveryTime)}
      </ViewField>
      <ViewField label="Currency">{quote.quoteCurrency || '-'}</ViewField>
      <ViewField label="Quotation Summary">
        <span style={{ whiteSpace: 'pre-wrap' }}>{quote.quoteSummary || '-'}</span>
      </ViewField>

      {/* Terms */}
      <SectionTitle icon="fa-list-check" title="Terms" />
      {quoteTermsRows.length === 0 ? (
        <div className="txtdsply text-muted mb-3">No terms</div>
      ) : (
        <div className="table-responsive mb-3">
          <table className="table table-bordered table-sm mb-0">
            <thead>
              <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                <th style={{ fontSize: '12px', width: '48px' }}>S.No</th>
                <th style={{ fontSize: '12px', minWidth: '160px' }}>Quote Term</th>
                <th style={{ fontSize: '12px', minWidth: '200px' }}>Quote Response</th>
              </tr>
            </thead>
            <tbody>
              {quoteTermsRows.map((term, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {term.quoteTerm}
                  </td>
                  <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {term.quoteResponse}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Materials */}
      <SectionTitle icon="fa-boxes-stacked" title="Materials" />
      {materials.length === 0 ? (
        <div className="txtdsply text-muted mb-3">No materials</div>
      ) : (
        <div className="table-responsive mb-3">
          <table
            className="table table-bordered table-sm mb-0"
            style={{ minWidth: '1100px' }}
          >
            <thead>
              <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                <th style={{ fontSize: '12px' }}>S.No</th>
                <th style={{ fontSize: '12px' }}>Material Code</th>
                <th style={{ fontSize: '12px' }}>Material Description</th>
                <th style={{ fontSize: '12px' }}>PR Number</th>
                <th style={{ fontSize: '12px' }}>HSN</th>
                <th style={{ fontSize: '12px' }}>UOM</th>
                <th style={{ fontSize: '12px' }}>Quantity</th>
                <th style={{ fontSize: '12px' }}>Basic Price</th>
                <th style={{ fontSize: '12px' }}>GST %</th>
                <th style={{ fontSize: '12px' }}>GST Value</th>
                <th style={{ fontSize: '12px' }}>Total Value</th>
                <th style={{ fontSize: '12px' }}>Technical Remarks</th>
              </tr>
            </thead>
            <tbody>
              {materials.map((row, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{row.materialCode || '-'}</td>
                  <td
                    style={{
                      maxWidth: '240px',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word'
                    }}
                  >
                    {row.materialDescription || '-'}
                  </td>
                  <td>{row.prNumber || row.prNo || '-'}</td>
                  <td>{row.hsn || row.hsnCode || '-'}</td>
                  <td>{row.uom || '-'}</td>
                  <td>{row.quantity ?? '-'}</td>
                  <td>{formatMaterialMoney(row.basicPrice)}</td>
                  <td>
                    {getMaterialGstPercent(row) != null
                      ? `${getMaterialGstPercent(row)}%`
                      : '-'}
                  </td>
                  <td>{getMaterialGstValueDisplay(row)}</td>
                  <td>{getMaterialTotalValueDisplay(row)}</td>
                  <td
                    style={{
                      maxWidth: '200px',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word'
                    }}
                  >
                    {row.technicalRemarks || '-'}
                  </td>
                </tr>
              ))}
              <tr style={{ backgroundColor: '#f4f6f9' }}>
                <td
                  colSpan={7}
                  className="text-right align-middle"
                  style={{ fontSize: '13px', fontWeight: 600 }}
                >
                  Gross Total
                  {quote.quoteCurrency ? ` (${quote.quoteCurrency})` : ''}
                </td>
                <td className="align-middle" style={{ fontSize: '13px', fontWeight: 600 }}>
                  {formatMaterialMoney(materialGrossTotals.grossTotalBasicPrice)}
                </td>
                <td />
                <td className="align-middle" style={{ fontSize: '13px', fontWeight: 600 }}>
                  {formatMaterialMoney(materialGrossTotals.grossGstValue)}
                </td>
                <td className="align-middle" style={{ fontSize: '13px', fontWeight: 600 }}>
                  {formatMaterialMoney(materialGrossTotals.grossTotalValue)}
                </td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Document */}
      <SectionTitle icon="fa-file-pdf" title="Document" />
      <ViewField label="PDF Link">
        {hasSalesQuotePdf(quote) ? (
          <button
            type="button"
            className="btn btn-link p-0 align-baseline"
            onClick={handleViewPdf}
            disabled={isOpeningPdf}
          >
            {isOpeningPdf ? (
              <>
                <i className="fa-solid fa-spinner fa-spin mr-1" />
                Opening...
              </>
            ) : (
              'View PDF'
            )}
          </button>
        ) : (
          '-'
        )}
      </ViewField>
       {ownerAttachments.length > 0 ? (
        <ViewField label="Owner Attachments">
          <AttachmentTileGrid
            attachments={ownerAttachments}
            downloadingS3Key={downloadingS3Key}
            onDownload={downloadOwnerAttachment}
            disabled={Boolean(downloadingS3Key)}
          />
        </ViewField>
      ) : null}
    </div>
  )
}

export default CustomerSalesQuoteView